import React, { useState } from 'react';
import { useCurrentLanguage, useAvailableLanguages, useLanguageProgress } from '../../hooks/useLanguage.js';
import { LanguageSwitcher } from '../language/LanguageSwitcher.js';
import { LoadingSpinner } from '../common/LoadingSpinner.js';

export function LanguageSettings() {
  const currentLanguage = useCurrentLanguage();
  const { languages, isLoading: isLoadingLanguages } = useAvailableLanguages();
  const { progress, isLoading: isLoadingProgress } = useLanguageProgress();

  const currentLang = languages.find((lang) => lang.code === currentLanguage);

  if (isLoadingLanguages || isLoadingProgress) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  const languagesStudied = Object.keys(progress);

  return (
    <div className="space-y-6">
      {/* Current Language Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Learning Language</h3>

        <div className="space-y-4">
          {/* Language Selector */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              Select Language
            </label>
            <LanguageSwitcher variant="dropdown" showName={true} />
          </div>

          {/* Current Language Details */}
          {currentLang && (
            <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
              <span className="text-5xl" aria-label={`${currentLang.name} flag`}>
                {currentLang.flag}
              </span>
              <div>
                <div className="text-xl font-semibold text-gray-900">{currentLang.name}</div>
                <div className="text-sm text-gray-600">{currentLang.nativeName}</div>
                {progress[currentLanguage!] && (
                  <div className="mt-2 flex items-center space-x-4 text-sm text-gray-600">
                    <span>Level {progress[currentLanguage!].level}</span>
                    <span>•</span>
                    <span>{progress[currentLanguage!].xp.toLocaleString()} XP</span>
                    <span>•</span>
                    <span>{progress[currentLanguage!].streak} day streak</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Languages Studied Section */}
      {languagesStudied.length > 1 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Your Language Progress
          </h3>

          <div className="space-y-4">
            {languagesStudied.map((langCode) => {
              const lang = languages.find((l) => l.code === langCode);
              const langProgress = progress[langCode];

              if (!lang || !langProgress) return null;

              return (
                <div
                  key={langCode}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                    langCode === currentLanguage
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">{lang.flag}</span>
                    <div>
                      <div className="font-medium text-gray-900">{lang.name}</div>
                      <div className="text-sm text-gray-600">
                        Level {langProgress.level} • {langProgress.xp.toLocaleString()} XP •{' '}
                        {langProgress.proficiencyLevel}
                      </div>
                    </div>
                  </div>

                  {langCode === currentLanguage && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-600 text-white">
                      Active
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Language Features Section */}
      {currentLang && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Language Features</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FeatureCard
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                </svg>
              }
              title="Voice Practice"
              description={currentLang.voiceSupported ? 'Available' : 'Not available'}
              enabled={currentLang.voiceSupported}
            />

            <FeatureCard
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              }
              title="Vocabulary"
              description={
                currentLang.hasInitialContent && currentLang.estimatedVocabularySize > 0
                  ? `~${currentLang.estimatedVocabularySize.toLocaleString()} words available`
                  : 'Content coming soon'
              }
              enabled={currentLang.hasInitialContent && currentLang.estimatedVocabularySize > 0}
            />

            <FeatureCard
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              title="Proficiency Levels"
              description={currentLang.proficiencyLevels.join(', ')}
              enabled={true}
            />

            <FeatureCard
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                  />
                </svg>
              }
              title="AI Conversations"
              description="Available"
              enabled={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  enabled: boolean;
}

function FeatureCard({ icon, title, description, enabled }: FeatureCardProps) {
  return (
    <div className={`p-4 rounded-lg border ${enabled ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-gray-100'}`}>
      <div className="flex items-start space-x-3">
        <div className={`${enabled ? 'text-primary-600' : 'text-gray-400'}`}>{icon}</div>
        <div className="flex-1">
          <h4 className={`font-medium ${enabled ? 'text-gray-900' : 'text-gray-500'}`}>{title}</h4>
          <p className={`text-sm ${enabled ? 'text-gray-600' : 'text-gray-400'}`}>{description}</p>
        </div>
        {!enabled && (
          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-600">
            Unavailable
          </span>
        )}
      </div>
    </div>
  );
}
